/**
 * #477 — mandatory professional disclaimers, told apart from the answer.
 *
 * The backend tells the model to write each disclaimer (medical, legal,
 * financial, mental health, safety) as its own final line starting with the
 * literal English word `Disclaimer:` — never translated, in every language —
 * so that marker is all the UI keys on. It never has to recognise translated
 * legal text. The BE twin is `soul_directives.disclaimer_line_text`.
 *
 * Pure helpers: `Message.jsx` renders a paragraph whose text starts with the
 * marker as the muted `assistant-disclaimer` block.
 */

/** The literal marker (PO decision #477 — not an i18n string). */
export const DISCLAIMER_PREFIX = 'Disclaimer:';

// Optional emphasis/code markers around the prefix (`**Disclaimer:** …`),
// the word (case-insensitive), the colon, then a real sentence.
const DISCLAIMER_TEXT_RE = /^[\s*_~`]*disclaimer[*_~`]*\s*:[\s*_~`]*[^\s*_~`]/i;

/** True when a paragraph's plain text is a disclaimer (starts with the marker). */
export function isDisclaimerText(text) {
    return typeof text === 'string' && DISCLAIMER_TEXT_RE.test(text);
}

/** Flatten a hast node's text (text nodes + every nested element, e.g. <em>). */
export function hastText(node) {
    if (!node) return '';
    if (node.type === 'text') return node.value || '';
    return (node.children || []).map(hastText).join('');
}

// A markdown LINE that starts a disclaimer (emphasis allowed before the word).
const DISCLAIMER_LINE_RE = /^[ \t]*[*_~`]*disclaimer[*_~`]*\s*:/i;

/**
 * Make every disclaimer line its own markdown paragraph.
 *
 * The prompt asks for a blank line before it, but a model that writes it on
 * the very next line ("…answer.\nDisclaimer: …") would otherwise produce ONE
 * paragraph (a soft break), and the block could not be told apart. Lines inside
 * ``` fences are left untouched.
 */
export function separateDisclaimerLines(markdown) {
    if (typeof markdown !== 'string' || !/disclaimer/i.test(markdown)) return markdown;
    const lines = markdown.split('\n');
    const out = [];
    let fenceOpen = false;
    for (const line of lines) {
        if (/^\s*```/.test(line)) {
            fenceOpen = !fenceOpen;
            out.push(line);
            continue;
        }
        if (!fenceOpen && DISCLAIMER_LINE_RE.test(line)) {
            const prev = out.length ? out[out.length - 1] : '';
            if (prev.trim() !== '') out.push('');
            out.push(line.replace(/^[ \t]+/, ''));
            // …and a blank line after, so a following line is not swallowed.
            out.push('');
            continue;
        }
        out.push(line);
    }
    // Trailing blank lines are meaningless in markdown; drop the padding.
    while (out.length && out[out.length - 1] === '') out.pop();
    return out.join('\n');
}
